import React, { ReactNode } from 'react';
import { createStyles } from '@/contexts/theme-context';
import { isWeb } from '@/utils/navigation';
import { LayoutSize, useLayoutContext } from '@/contexts/layout-context';
import { SidebarLayoutShell } from '@experiences/templates/sidebar-layout-shell';
import { SidenavItem } from '@/domain/navigation/types';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { View } from 'react-native';

interface AppShellTemplateProps {
  children: ReactNode;
  navItems: SidenavItem[];
  navItemsLoading?: boolean;
  sidebarFooter?: ReactNode;
  sidebarWidth?: number;
}

/**
 * Experience-agnostic nav shell.
 *
 * Renders a sidebar on desktop/tablet, and bare safe-area container on mobile
 * (for React Navigation to populate).
 *
 * Sales Hub screens use {@link SalesHubShellTemplate}.
 */
export const AppShellTemplate: React.FC<AppShellTemplateProps> = ({
  children,
  navItems,
  navItemsLoading,
  sidebarFooter,
  sidebarWidth,
}) => {
  const [styles] = useStyles();
  const insets = useSafeAreaInsets();

  const { layoutSize } = useLayoutContext();
  const isMobile = !isWeb && layoutSize === LayoutSize.SMALL;

  if (isMobile) {
    return (
      <View
        style={[
          styles.mobileContainer,
          {
            paddingLeft: insets.left,
            paddingRight: insets.right,
            paddingTop: insets.top,
          },
        ]}
      >
        {children}
      </View>
    );
  }

  return (
    <SidebarLayoutShell
      navItems={navItems}
      navItemsLoading={navItemsLoading}
      sidebarFooter={sidebarFooter}
      sidebarWidth={sidebarWidth}
    >
      {children}
    </SidebarLayoutShell>
  );
};

const useStyles = createStyles((theme) => ({
  mobileContainer: {
    backgroundColor: theme.sk.color.bg.primary.default,
    flex: 1,
  },
}));
